import { useEffect, useMemo } from 'react'
import { useController, UseFormReturn } from 'react-hook-form'

import { ModalTypes, useModal } from '@/components/Modal'
import { LogLocationType, MileageLossCode } from '@/types/logs'
import { Input } from '@/ui/Input'
import { InputFormValues } from '@/modules/CombinedLogs/types'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'
import { parseLocationStopValue } from '@/modules/CombinedLogs/helpers/location'
import { useGetTrips } from '@/modules/CombinedLogs/api/useGetTrips'
import { formatTripValues } from '@/modules/CombinedLogs/helpers/formatTripValues'
import { useBusStops } from '@/pages/UnfinishedRecords/hooks/useBusStops'
import { getSelectedStops } from '@/modules/CombinedLogs/helpers/getSelectedStops'
import { calculateMileage } from '@/modules/CombinedLogs/helpers/calculateMileage'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'

import styles from './TableFormLocationInput.module.scss'

import BridgeIcon from '../../../../assets/bridge.svg'

interface Props {
    form: UseFormReturn<InputFormValues>
    isLinked?: boolean
}

const MAX_LENGTH = 200

export const TableFormLocationInput = ({ form, isLinked = false }: Props) => {
    const { field: locationField } = useController({ control: form.control, name: 'locationFrom' })
    const { field: directionField } = useController({ control: form.control, name: 'direction' })

    const { selectedTrips, isBulkCurtailment, locationType, setLocationType } = useCombinedLogsTableFormStateStore()

    const code = form.watch('code')
    const route = form.watch('routes')
    const trip = form.watch('trip')
    const tripAsNumber = trip ? parseInt(trip, 10) : null
    const startDirection = tripAsNumber ? (tripAsNumber % 2 === 1 ? 'outbound' : 'inbound') : 'outbound'

    const tripTo = form.watch('bulkCurtailmentTripTo')
    const rtnTrips = form.watch('rtnTrips')

    const { data: trips } = useGetTrips(trip)

    const { openModal } = useModal()

    const locationFrom = form.watch('locationFrom')
    const locationTo = form.watch('locationTo')
    const isZeroMileage = form.watch('isZeroMileage')

    const { getRouteBusStops } = useBusStops()
    const routeStops = getRouteBusStops(route)
    const allStops = routeStops ? [...routeStops.outbound, ...routeStops.inbound] : []

    const isDefaultLocationOption = !(code && code in MileageLossCode)

    const directionOptions = useMemo(() => {
        if (!routeStops || !routeStops.inbound.length || !routeStops.outbound.length) {
            return []
        }

        const outboundRouteEnd = routeStops.outbound.at(-1)
        const inboundRouteEnd = routeStops.inbound.at(-1)

        return [
            {
                id: '0',
                label: `Towards ${outboundRouteEnd?.stopName}`,
                value: `Towards ${outboundRouteEnd?.stopName}`,
            },
            {
                id: '1',
                label: `Towards ${inboundRouteEnd?.stopName}`,
                value: `Towards ${inboundRouteEnd?.stopName}`,
            },
            {
                id: '2',
                label: `Both directions`,
                value: `Both directions`,
            },
        ]
    }, [routeStops])

    const locationFromName = useMemo(() => {
        return (
            locationFrom &&
            allStops.find((item) => {
                const { stopNumber, direction } = parseLocationStopValue(locationFrom)
                return item.stopNumber === stopNumber && item.direction === direction
            })?.stopName
        )
    }, [locationFrom, allStops])

    const locationToName = useMemo(() => {
        return (
            locationTo &&
            allStops.find((item) => {
                const { stopNumber, direction } = parseLocationStopValue(locationTo)
                return item.stopNumber === stopNumber && item.direction === direction
            })?.stopName
        )
    }, [locationTo, allStops])

    const selectedStops = useMemo(() => {
        if (!locationFrom || !locationTo || !locationType) {
            return []
        }

        return getSelectedStops(allStops, locationFrom, locationTo, locationType)
    }, [allStops, locationFrom, locationTo, locationType])

    const allSelectedStops = useMemo(() => {
        if (!locationFrom || !locationTo || !locationType) {
            return []
        }

        return getSelectedStops(allStops, locationFrom, locationTo, LogLocationType.All)
    }, [allStops, locationFrom, locationTo, locationType])

    const isLowBridgeDetected = useMemo(() => {
        if (!locationFrom || !locationTo) {
            return false
        }

        if (selectedStops.length < 2) {
            return false
        }

        const from = allStops.find((item) => {
            const { stopNumber, direction } = parseLocationStopValue(locationFrom)
            return item.stopNumber === stopNumber && item.direction === direction
        })

        const to = allStops.find((item) => {
            const { stopNumber, direction } = parseLocationStopValue(locationTo)
            return item.stopNumber === stopNumber && item.direction === direction
        })

        const isAllStops = !from?.isCurtailmentStop || !to?.isCurtailmentStop

        return selectedStops.filter((stop) => isAllStops || stop.isCurtailmentStop).some((stop) => stop.isNearLowBridge)
    }, [selectedStops, locationFrom, locationTo, allStops])

    const handleEditLocation = (focusOn: 'to' | 'from') => {
        if (!route || !routeStops) {
            return
        }

        const callback = ({
            from,
            to,
            type,
            mileage,
            isZeroMileage,
        }: {
            from: string
            to: string
            type: LogLocationType
            mileage: string
            isZeroMileage: boolean
        }) => {
            form.setValue('locationFrom', from, { shouldDirty: true })
            form.setValue('locationTo', to, { shouldDirty: true })
            form.setValue('mileage', mileage, { shouldDirty: true })
            form.setValue('isZeroMileage', isZeroMileage ? 'true' : 'false', { shouldDirty: true })

            setLocationType(type)

            const parsedLocationFrom = parseLocationStopValue(from)
            const parsedLocationTo = parseLocationStopValue(to)

            if (parsedLocationFrom.direction !== parsedLocationTo.direction && trip && !rtnTrips && trips) {
                const firstRtnTrip = trips.outbound[0]
                firstRtnTrip && form.setValue('rtnTrips', formatTripValues([firstRtnTrip]))
            }
        }

        openModal(ModalTypes.editLocationModal, {
            route,
            direction: startDirection,
            isZeroMileage: isZeroMileage === 'true',
            from: locationFrom,
            to: locationTo,
            focusOn: locationFrom || locationTo ? '' : focusOn,
            callback,
        })
    }
    useEffect(() => {
        if (!isBulkCurtailment || (!trip && !tripTo) || !selectedTrips.length || !allSelectedStops.length) {
            return
        }
        const { stopNumber, direction } = parseLocationStopValue(locationFrom || '')
        const updateMileageLoss = calculateMileage(allSelectedStops, selectedTrips, stopNumber, direction)
        form.setValue('mileage', updateMileageLoss, { shouldDirty: true })
    }, [isBulkCurtailment, trip, tripTo, allSelectedStops, selectedTrips])

    if (isDefaultLocationOption && !isLinked) {
        return (
            <div className={styles.container}>
                <Input
                    variant="table"
                    type="text"
                    transparent
                    fullWidth
                    placeholder="Location"
                    value={locationField.value}
                    maxLength={MAX_LENGTH}
                    onChange={locationField.onChange}
                />
                {code && !!directionOptions.length && (
                    <SuggestionInputTable
                        value={directionField.value}
                        onChange={directionField.onChange}
                        variant="table"
                        type="text"
                        transparent
                        fullWidth
                        options={directionOptions}
                        placeholder="Direction"
                    />
                )}
            </div>
        )
    }

    return (
        <div className={styles.root}>
            <div className={styles.container}>
                <div className={styles.info} onClick={() => handleEditLocation('from')}>
                    <span>From:</span> {locationFromName && <span className={styles.text}>{locationFromName}</span>}
                </div>
                <div className={styles.info} onClick={() => handleEditLocation('to')}>
                    <span>To:</span> {locationToName && <span className={styles.text}>{locationToName}</span>}
                </div>
            </div>
            {isLowBridgeDetected && (
                <div className={styles.lowBridgeContainer}>
                    <BridgeIcon /> Low bridge nearby
                </div>
            )}
        </div>
    )
}
